import { appBarMeta } from "@constants/appbar";
import { events } from "@constants/tracking";
import { useSessionConfigDialog } from "@contexts/DialogContext/dialogs/SessionConfigDialog";
import { usePurchaseDialog } from "@contexts/DialogContext/dialogs/purchase";
import { getNumAttempted } from "@helpers/content/tag";
import { useAppTemplate } from "@hooks/useAppTemplate";
import { useCustomNavigation } from "@hooks/useCustomNavigation";
import useMixedItemDist from "@hooks/useMixedItemDist";
import { isWeb } from "@memorang/helpers/platform";
import type { MixedItemDist } from "@memorang/types";
import Box from "@memorang/ui/components/Box";
import ScrollContainer from "@memorang/ui/components/ScrollContainer";
import StudyPackApplet from "@memorang/ui/components/StudyPackApplet";
import { TagsSectionListSkeleton } from "@memorang/ui/components/StudyPackApplet/TagsSectionListSkeleton";
import { useDevice } from "@memorang/ui/useDevice";
import { trackCustomEvent } from "analytics";
import { useState } from "react";
import { FeedbackDialogContainer } from "../../../containers/FeedbackDialogContainer";
import { NestedExamChipsContainer } from "../containers/NestedExamChipsContainer";
import { getDashboardData } from "../helpers/dashboard-data";
import { useDonutProgress } from "../hooks/use-donut-progress";
import { useHasAccess } from "../hooks/useHasAccess";
import { AppBar } from "../layouts/AppBar";

const StudyPackContainer = () => {
	const { data: studyPackDistribution } = useMixedItemDist({
		action: "GET_STUDYPACK_DISTRIBUTION",
	});
	const { isMobile } = useDevice();

	if (!studyPackDistribution) {
		return (
			<>
				<AppBar title={appBarMeta["product-home"].title} />
				<Box position="relative" flexGrow={1} height="100%">
					<ScrollContainer
						maxWidth="sm"
						contentContainerStyle={{
							paddingHorizontal: isMobile ? 0 : 16,
							paddingBottom: 96,
						}}
					>
						<NestedExamChipsContainer
							disabled
							containerStyle={{ paddingTop: 24 }}
						/>
						<TagsSectionListSkeleton />
					</ScrollContainer>
				</Box>
			</>
		);
	}

	return (
		<StudyPackRenderer
			studyPackDistribution={studyPackDistribution}
			headerTitle={appBarMeta["product-home"].title}
		/>
	);
};

export default StudyPackContainer;

const StudyPackRenderer = ({
	studyPackDistribution,
	headerTitle,
}: {
	studyPackDistribution: MixedItemDist;
	headerTitle: string;
}) => {
	const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
	const { handleFeedbackNavigation } = useCustomNavigation();

	const toggleShowFeedbackDialog = () => {
		setShowFeedbackDialog((prev) => !prev);
	};
	const { showPurchase } = usePurchaseDialog();
	const { showSessionConfig } = useSessionConfigDialog();

	const { hasStudyPackPurchaseAccess } = useHasAccess();

	const data = studyPackDistribution;

	const handleNavigateToIAP = () => {
		trackCustomEvent({
			eventName: events.upgradeChipClicked,
		});

		showPurchase({
			productType: "STUDY_PACK",
			routeParams: { numItems: numItems.toString() },
		});
	};
	const {
		distribution,
		productImage,
		productTitle,
		sectionTags,
		numItems,
		freeContentBanner,
		freeSkillTagIds,
	} = getDashboardData(
		data,
		handleNavigateToIAP,
		true,
		hasStudyPackPurchaseAccess,
	);

	const { donutProgressData, donutProgressColorScale } =
		useDonutProgress(distribution);

	const { isLanguageLearningApp } = useAppTemplate();

	const completionSectionTags =
		isLanguageLearningApp && donutProgressColorScale
			? sectionTags.map((section) => ({
					...section,
					tags: section.tags.map((tag) => ({
						...tag,
						donutScore: getNumAttempted(tag.distribution),
						donutColorScale: donutProgressColorScale,
					})),
				}))
			: sectionTags;

	//TODO:Below hooks look ugly to get just the leaderboard work, instead the api should return the stats

	const handleClickListItem = (selectedTagId: number, locked?: boolean) => {
		if (locked) {
			handleNavigateToIAP();
		} else {
			showSessionConfig({
				selectedTagIds: [selectedTagId],
				preferRecommendedTab: true,
			});
		}
	};

	return (
		<>
			<AppBar title={headerTitle} />
			<StudyPackApplet
				renderNestedExamChips={() => (
					<NestedExamChipsContainer containerStyle={{ paddingTop: 24 }} />
				)}
				distribution={distribution}
				donutProgressData={donutProgressData}
				donutProgressColorScale={donutProgressColorScale}
				productTitle={productTitle}
				productImage={productImage}
				sectionTags={completionSectionTags}
				handleClickListItem={handleClickListItem}
				handleFeedbackNavigation={() =>
					handleFeedbackNavigation(toggleShowFeedbackDialog)
				}
				freeContentBanner={
					freeContentBanner
						? {
								...freeContentBanner,
								onPress: () =>
									showSessionConfig({ selectedTagIds: freeSkillTagIds }),
							}
						: undefined
				}
			/>
			{isWeb && showFeedbackDialog && (
				<FeedbackDialogContainer
					visible={showFeedbackDialog}
					onDismiss={toggleShowFeedbackDialog}
				/>
			)}
		</>
	);
};
